import { Button } from '@hofsync/design-system';
import { LayoutGrid, List } from 'lucide-react';
import type { TileViewMode } from '../hooks/useTileViewMode';
import './TileViewToggle.css';

interface TileViewToggleProps {
    /** Currently active view mode. */
    mode: TileViewMode;
    /** Called with the mode the user picked. */
    onChange: (mode: TileViewMode) => void;
    /** Optional extra class on the group wrapper (e.g. header-row alignment). */
    className?: string;
}

/**
 * Grid/list segmented toggle for the tile-grid starting surfaces (#1893).
 *
 * Built at the frontend level from two shadcn `Button size="icon"` + the
 * lucide `LayoutGrid` / `List` icons — the instantly-recognisable
 * Finder/Drive idiom — so we avoid vendoring a new DS ToggleGroup primitive
 * (which would drag in the Tailwind rebuild + committed dist/styles.css gate).
 *
 * a11y: a labelled `role="group"`; each button is a native, focusable,
 * `aria-label`led `<button type="button">` with `aria-pressed` tracking the
 * active mode.
 */
export default function TileViewToggle({ mode, onChange, className }: TileViewToggleProps) {
    const wrapperClass = ['tile-view-toggle', className].filter(Boolean).join(' ');
    return (
        <div className={wrapperClass} role="group" aria-label="Ansicht wählen">
            <Button
                type="button"
                size="icon"
                variant={mode === 'card' ? 'secondary' : 'ghost'}
                aria-pressed={mode === 'card'}
                aria-label="Kachelansicht"
                title="Kachelansicht"
                onClick={() => onChange('card')}
            >
                <LayoutGrid size={18} aria-hidden />
            </Button>
            <Button
                type="button"
                size="icon"
                variant={mode === 'compact' ? 'secondary' : 'ghost'}
                aria-pressed={mode === 'compact'}
                aria-label="Kompakte Liste"
                title="Kompakte Liste"
                onClick={() => onChange('compact')}
            >
                <List size={18} aria-hidden />
            </Button>
        </div>
    );
}
